import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useMemo, useRef, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';
import { dateDistanceToNowAsText } from '~/shared/date/date';
import { IconChevronDownSmall, IconChevronTopSmall } from '~/ui/core/icons';

import type { JobItemDetails } from '../api/use-job-item-details';
import { GenericAssignReviewerBadge } from '../components/ReviewerComponents';
import { ActionBarAuditTrailTable, ActionBarVersionComment } from './audit-table/ActionBarAuditTrail';
import { AuditDisplayShell } from './audit-table/AuditDisplayShell';
import { AuditTrailTable } from './audit-table/AuditTrailTable';
import { buildAuditTrailSteps, deriveStatus } from './audit-table/audit-trail';
import { CollapsedAuditAction } from './audit-table/CollapsedAuditAction';
import { computeStatusColumnWidth } from './audit-table/status-column-width';
import { canEditVersionNote, resolveVersionNote } from './audit-table/version-note';
import { useAuditTableLabels } from './use-audit-table-labels';

export { AuditTableSkeleton } from './audit-table/AuditTableSkeleton';

type AuditTableProps = {
    readonly architectureId: string;
    readonly currentVersion: JobProfileVersion;
    readonly isCollapsible?: boolean;
    readonly isArchived?: boolean;
    readonly item: JobItemDetails;
    readonly onInviteReviewer?: () => void;
};

export const AuditTable: FunctionComponent<AuditTableProps> = ({
    architectureId,
    currentVersion,
    isArchived = false,
    isCollapsible = false,
    item,
    onInviteReviewer,
}) => {
    const { applicationState } = useApplicationContext();
    const { language } = applicationState;

    const status = deriveStatus(currentVersion);
    const steps = buildAuditTrailSteps(status, currentVersion, item);
    const note = resolveVersionNote(currentVersion.comment);
    const canEditNote = canEditVersionNote(currentVersion, item);

    const labels = useAuditTableLabels(status);

    const statusColumnWidth = useMemo(() => computeStatusColumnWidth(labels.allStepLabels), [labels.allStepLabels]);

    const columnWidths: readonly string[] = [`${statusColumnWidth}px`, 'minmax(0, 1fr)', 'minmax(9rem, max-content)'];

    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditingNote, setIsEditingNote] = useState(false);
    const enterEditMode = (): void => {
        setIsEditingNote(true);
        if (isCollapsible) {
            setIsExpanded(true);
        }
    };
    const exitEditMode = (): void => setIsEditingNote(false);

    const editingScopeRef = useRef<{ readonly itemId: string; readonly version: number }>({
        itemId: item.id,
        version: currentVersion.version,
    });
    if (editingScopeRef.current.itemId !== item.id || editingScopeRef.current.version !== currentVersion.version) {
        editingScopeRef.current = { itemId: item.id, version: currentVersion.version };
        if (isEditingNote) {
            setIsEditingNote(false);
        }
    }

    const formatTimestamp = (iso: string | null): string => {
        if (iso === null) {
            return '—';
        }
        return dateDistanceToNowAsText(Date.now(), language.date.locale, iso).unwrapOr('—');
    };

    const currentStep = steps.find((step) => step.state === 'current') ?? steps.find((step) => step.id === 'draft');
    const versionComment =
        isCollapsible && (is.nonEmptyString(note) || canEditNote) ? (
            <ActionBarVersionComment
                architectureId={architectureId}
                canEditNote={canEditNote}
                currentVersion={currentVersion}
                isEditingNote={isEditingNote}
                item={item}
                note={note}
                onEnterEditMode={enterEditMode}
                onExitEditMode={exitEditMode}
            />
        ) : null;
    const collapsedAction = (
        <CollapsedAuditAction
            architectureId={architectureId}
            currentVersion={currentVersion}
            isArchived={isArchived}
            item={item}
            onInviteReviewer={onInviteReviewer}
            status={status}
            toggle={{
                icon: <IconChevronDownSmall size={16} />,
                label: 'Show audit trail',
                onClick: () => setIsExpanded(true),
            }}
        />
    );
    const expandedAction = (
        <CollapsedAuditAction
            architectureId={architectureId}
            currentVersion={currentVersion}
            isArchived={isArchived}
            item={item}
            onInviteReviewer={onInviteReviewer}
            status={status}
            toggle={{
                icon: <IconChevronTopSmall size={16} />,
                label: 'Hide audit trail',
                onClick: () => setIsExpanded(false),
            }}
        />
    );

    const table = (
        <AuditTrailTable
            architectureId={architectureId}
            canEditNote={canEditNote}
            columnWidths={columnWidths}
            currentVersion={currentVersion}
            formatTimestamp={formatTimestamp}
            isArchived={isArchived}
            isEditingNote={isEditingNote}
            item={item}
            labels={labels}
            note={note}
            status={status}
            steps={steps}
            onEnterEditMode={enterEditMode}
            onExitEditMode={exitEditMode}
            onInviteReviewer={onInviteReviewer}
            showHeader={!isCollapsible}
        />
    );

    if (!isCollapsible) {
        return table;
    }

    if (is.undefined(currentStep)) {
        return table;
    }

    const visibleStepIds = isExpanded ? undefined : ([currentStep.id] as const);
    const actionBarTable = (
        <ActionBarAuditTrailTable
            commentNode={versionComment}
            formatTimestamp={formatTimestamp}
            labels={labels}
            reviewerPill={
                <GenericAssignReviewerBadge
                    badgeClassName="!h-[22px] tracking-tight hover:!border-gray-400"
                    chromeVariant="gray"
                    disabled={isArchived}
                    item={item}
                    itemType={item.type}
                    jobArchitectureId={architectureId}
                    jobClusterId={item.parentId}
                    onInvite={onInviteReviewer}
                    onlyAssignReviewer={true}
                    showOnlyWhenUnassigned={false}
                />
            }
            status={status}
            steps={steps}
            trailingNodeForStep={
                isExpanded ? undefined : (step) => (step.id === currentStep.id ? collapsedAction : undefined)
            }
            visibleStepIds={visibleStepIds}
        />
    );

    return <AuditDisplayShell footerAction={isExpanded ? expandedAction : null}>{actionBarTable}</AuditDisplayShell>;
};
